import clsx from 'clsx'
import {
  UseControllerProps,
  useController,
  useFormContext,
} from 'react-hook-form'
import getDerivedId from '@ancon/wildcat-ui/web/shared/utils/getDerivedId'

import { AppTestIds } from '../constants'

import BodyText from './BodyText'
import Input, { InputProps } from './input/Input'
import FormInputErrorLabel from './FormInputErrorLabel'
import styles from './FormInput.module.scss'

type ReactHookFormRules = UseControllerProps['rules']

interface FormInputProps extends InputProps {
  /* Name of the field in form state */
  fieldName: string
  /* Text label of the input */
  label?: string
  /* Validation rules of the input */
  rules?: ReactHookFormRules
  /* Customized Error Message */
  renderCustomErrorComponent?: (errorMessage: string) => React.ReactNode
  /* Class name of the container */
  containerClassName?: string
  /* Class name for the label */
  labelClassName?: string
  /* Whether input is required */
  required?: boolean
  /* Whether to trim whitespace */
  noWhiteSpaces?: boolean
  /* Whether to show error if touched */
  showErrorOnTouched?: boolean
  /* Allow only numbers to be entered */
  onlyAllowNumbers?: boolean
  /* Class name for the spacer */
  spacerClassName?: string
}

function FormInput({
  fieldName,
  label,
  rules,
  renderCustomErrorComponent,
  readOnly,
  containerClassName,
  labelClassName,
  required,
  noWhiteSpaces = false,
  showErrorOnTouched = true,
  onlyAllowNumbers = false,
  spacerClassName,
  onBlur: onInputBlur,
  ...inputProps
}: FormInputProps) {
  const { setValue } = useFormContext()

  const id = getDerivedId({ ...inputProps, label, name: fieldName })

  const {
    field: { onBlur: onFieldBlur, onChange, value, ref },
    fieldState: { error, isTouched },
  } = useController({
    name: fieldName,
    rules,
  })

  const hasError = !!error && (!showErrorOnTouched || isTouched)

  function generateErrorText() {
    if (!hasError) return ''

    return `${error.message}`
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { pattern, value: nextValue } = event.target

    let text = nextValue

    // Check whether text only contains whitespace
    if (text && !text.replace(/\s/g, '').length) {
      return
    }

    if (text.length) {
      if (noWhiteSpaces) {
        text = text.replace(/\s/g, '')
      }

      if (onlyAllowNumbers) {
        text = text.replace(/\D/g, '')
      }

      if (pattern) {
        const regex = new RegExp(pattern)
        // If the text does not match the pattern, skip updating the value
        if (!regex.test(text)) {
          return
        }
      }
    }

    onChange(text)
    setValue(fieldName, text)
  }

  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    onFieldBlur()
    onInputBlur?.(event)
  }

  return (
    <div className={clsx(styles.container, containerClassName)}>
      {label && (
        <BodyText
          as="label"
          color="body-1"
          className={clsx(styles.label, labelClassName)}
          htmlFor={id}
        >
          {label}
          {required && <span>{' *'}</span>}
        </BodyText>
      )}
      <Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...inputProps}
        ref={ref}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={hasError}
        readOnly={readOnly}
        className={clsx(!hasError && styles.valid, readOnly && styles.readOnly)}
        id={id}
      />
      {hasError ? (
        (renderCustomErrorComponent &&
          renderCustomErrorComponent(generateErrorText())) || (
          <FormInputErrorLabel
            htmlFor={fieldName}
            error={error?.message}
            data-testid={AppTestIds.FormInputErrorLabel + fieldName}
          />
        )
      ) : (
        <div className={clsx(styles.spacer, spacerClassName)} />
      )}
    </div>
  )
}

export default FormInput
